import type { SelectOption } from '../components/inputs/selectShared';

/**
 * The titles SEEDED beside a first name — what the picker opens on, not what it accepts.
 *
 * **The reader may type one that is not here** (31 Oct 2026). Every salutation segment passes
 * `allowCustom`, so the typed text is committed verbatim, and the server validates SHAPE rather
 * than membership (`Salutations.IsWellFormed`: at most 20 characters, at least one letter).
 * That reverses the 09 Oct 2026 call that this list was closed — a call that named its own
 * expiry, "revisit it the day somebody asks for a title that is not here".
 *
 * **Still a mirror of `Salutations.All` on the server**, and still worth keeping in step — but
 * drift is now cosmetic (one list opens on a title the other does not) rather than a save that
 * fails. Add a title here and on the server in the same change.
 *
 * Not fetched from an endpoint: seven fixed strings do not earn a request on every form open.
 * A catalog table was declined and stays declined — the cost is that a typed title is not
 * offered to the NEXT form, so "Engr" and "Engr." can both end up stored. If that starts to
 * matter, the answer is the catalog, not a longer list here.
 */
export const SALUTATIONS: readonly string[] = ['Mr.', 'Ms.', 'Mrs.', 'Dr.', 'Engr.', 'Atty.', 'Hon.'];

/**
 * As picker options. `sort="none"` at the call site keeps this order, which is deliberate:
 * the four general titles first, then the professional ones. Alphabetising would open the list
 * on "Atty.".
 */
export const SALUTATION_OPTIONS: SelectOption[] = SALUTATIONS.map((value) => ({
  value,
  label: value,
}));

/**
 * The salutation segment's width — FIVE CHARACTERS (the longest title, `Engr.`/`Atty.`) plus the
 * seamless control's own `pl-2 pr-2`, in the INPUT's size (`text-sm`, so `ch` measures the same font
 * the value is drawn in). Product call, 13 Oct 2026; it was `w-24`. ONE definition for every name row —
 * eight forms each carried their own copy.
 */
export const SALUTATION_SEGMENT_WIDTH = 'w-[calc(5ch+1rem)] shrink-0 text-sm';

/**
 * What the EMPTY segment shows. The segment's own label, "Salutation", does not fit five
 * characters; "Title" does, and is the word the field's own help text already uses for it.
 */
export const SALUTATION_EMPTY_LABEL = 'Title';
